"use client";

import React, { useState, useEffect, useRef } from "react";
import Footer from "../components/Footer";
import { Link, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { products } from "../prod";
import { Product } from "../lib/types";
import { motion } from "framer-motion";

const ProductView = ({ step = 300 }) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const navigate = useNavigate();

  function got(path: string) {
    navigate("/product/" + path);
    window.scrollTo(0, 0);
  }
  // const getQuantity = useRef(0);
  const add = () => {
    setQuantity(quantity + 1);
  };

  const minus = () => {
    setQuantity(quantity - 1);
  };

  const updateEdges = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const epsilon = 4;
    setAtStart(el.scrollLeft <= epsilon);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - epsilon);
  };

  useEffect(() => {
    updateEdges();
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => updateEdges();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateEdges);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateEdges);
    };
  }, []);

  const scrollBy = (dx: number) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  };

  const productTitle = useParams();

  let productList = products;

  const currentProduct = productList.filter((obj) => {
    return obj.title === productTitle.title;
  });

  function getRandomElements(array: Product, numElements: number) {
    if (numElements > array.length) {
      return null;
    }
    const randomElements = [];
    const seenIndexes = new Set();
    while (randomElements.length < numElements) {
      let randomIndex = Math.floor(Math.random() * array.length);
      while (seenIndexes.has(randomIndex)) {
        randomIndex = Math.floor(Math.random() * array.length);
      }
      seenIndexes.add(randomIndex);
      randomElements.push(array[randomIndex]);
    }
    return randomElements;
  }

  getRandomElements(productList, 6);
  const randomProducts = getRandomElements(productList, 6);

  return (
    <div className="text-black bg-white w-full flex flex-col justify-center items-center gap-4">
      <div className="h-[8vh] bg-black w-full"></div>
      <div className="min-h-[92vh] w-11/12 md:w-10/12 py-2 flex flex-col gap-8">
        <Link
          to="/"
          className="w-max text-xs font-bold tracking-wide text-black/60 hover:text-black"
        >
          GO BACK
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="flex-1 flex flex-col gap-[4rem]"
        >
          {currentProduct.map((product) => {
            return (
              <div
                key={product.id}
                className="flex md:flex-row flex-col bg-yellow-600x items-center gap-5 lg:gap-[4rem] md:min-h-[40vh] min-h-[40vh]"
              >
                <div className="bg-secondary rounded overflow-hidden sm:w-1/2 w-3/4 h-full min-h-[30vh] lg:size-[500px] grid place-items-center shadow-xl">
                  <img
                    src={product.img}
                    alt=""
                    className="w-[200px] md:w-[300px]"
                  />
                </div>
                <div className="md:p-2 flex-1 grid place-items-center bg-blue-600x h-full">
                  <div className="w-full bg-yellow-600x">
                    {product.new === true && (
                      <p className="text-xs text-primary/60 tracking-wide md:tracking-widest sm:tracking-[.5rem] mb-3 uppercase font-medium">
                        New product
                      </p>
                    )}
                    <h1 className="text-3xl lg:text-[3rem] lg:leading-[3.5rem] bg-teal-600x lg:w-6/12 text-start font-bold mb-4 sm:mb-2">
                      {product.name}
                    </h1>
                    <p className="text-xs text-black/90 sm:font-medium bg-teal-600x mb-6 sm:mb-4 tracking-wide lg:w-10/12 leading-5 text-start">
                      Experience natural, lifelike audio and exceptional build
                      quality made for the passionate music enthusiast.
                    </p>
                    <p className="font-semibold text-sm mb-6 sm:mb-4 text-black/80">
                      $ {product.price}.00
                    </p>
                  </div>
                  <div className="flex items-center gap-4 bg-teal-950x w-full">
                    <div className="w-1/2 bg-secondary px-6x *:py-3 items-center gap-3x text-[10px] font-semibold uppercase tracking-wide flex justify-between">
                      <div
                        onClick={minus}
                        className={`${
                          quantity <= 0
                            ? "pointer-events-none bg-white/60"
                            : "pointer-events-auto"
                        } hover:bg-primary w-1/3 grid place-items-center h-full cursor-pointer hover:text-white`}
                      >
                        {/* <i
                          onClick={minus}
                          className={`fa-solid fa-minus ${
                            quantity <= 0
                              ? "pointer-events-none bg-white/60"
                              : "pointer-events-auto"
                          } hover:bg-primary bg-red-700 text-center hover:text-white p-3.5x cursor-pointer`}
                        ></i> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 12h14"
                          />
                        </svg>
                      </div>
                      <input
                        value={quantity}
                        disabled
                        className="w-1/3 text-center outline-none bg-teal-700x bg-transparent outline-0 text-[10px]"
                      />
                      <div
                        onClick={add}
                        className={`${
                          quantity >= 10
                            ? "pointer-events-none bg-white/60"
                            : "pointer-events-auto"
                        } hover:bg-primary w-1/3 grid place-items-center h-full cursor-pointer hover:text-white`}
                      >
                        {/* <i
                          onClick={add}
                          className={`fa-solid fa-plus ${
                            quantity >= 10
                              ? "pointer-events-none bg-white/60"
                              : "pointer-events-auto"
                          } hover:bg-primary bg-fuchsia-700 hover:text-white p-3.5x cursor-pointer`}
                        ></i> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-4 font-semibold"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4.5v15m7.5-7.5h-15"
                          />
                        </svg>
                      </div>
                    </div>
                    <button className="bg-primary w-1/2 text-white hover:bg-white hover:text-primary border border-primary text-[10px] px-6 py-3 font-semibold uppercase tracking-wide">
                      ADD TO CART
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="flex lg:flex-row flex-col-reverse justify-between gap-6 lg:gap-10 min-h-[40vh]">
            <div className="lg:w-7/12 flex flex-col gap-2 bg-red-700x">
              <p className="text-xl lg:text-2xl text-start font-semibold lg:font-bold tracking-tight uppercasex capitalize">
                Features
              </p>
              <div className="flex-1 lg:mt-3 text-start flex flex-col gap-4 relative">
                <div
                  className={`relative transition-all duration-500 ${
                    expanded
                      ? "max-h-none"
                      : "max-h-32 md:max-h-max overflow-hidden md:overflow-visible"
                  }`}
                >
                  <p className="text-xs md:text-sm font-medium lg:w-10/12 text-black/80 !leading-7">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                    do eiusmod tempor incididunt ut labore et dolore magna
                    aliqua. Ut enim ad minim veniam, quis nostrud exercitation
                    ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    Duis aute irure dolor in reprehenderit in voluptate velit
                    esse cillum dolore eu fugiat nulla pariatur. Excepteur sint
                    occaecat cupidatat non proident, sunt in culpa qui officia
                    deserunt mollit anim id est laborum. <br />
                    Duis aute irure dolor in reprehenderit in voluptate velit
                    esse cillum dolore eu fugiat nulla pariatur. Excepteur sint
                    occaecat cupidatat non proident, sunt in culpa qui officia
                    deserunt mollit anim id est laborum.
                  </p>
                  {!expanded && (
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white via-white/70 to-transparent flex justify-center items-end pb-2 md:hidden">
                      <button
                        onClick={() => setExpanded(true)}
                        className="text-xs font-semibold text-primary underline"
                      >
                        Read more
                      </button>
                    </div>
                  )}
                </div>
                {expanded && (
                  <button
                    onClick={() => setExpanded(false)}
                    className="mt-2 text-xs font-semibold text-primary underline md:hidden self-center"
                  >
                    Show less
                  </button>
                )}
              </div>
            </div>
            <div className="lg:w-5/12 bg-teal-600x flex flex-col gap-4">
              <p className="text-xl lg:text-2xl text-start font-bold tracking-tight uppercasex capitalize">
                In the box
              </p>
              <div className="lg:mt-3 flex-1 flex flex-col gap-3">
                <div className="flex text-xs font-medium items-center gap-4">
                  <span className="text-primary w-[15px]">1x</span>
                  <p className="text-black/80">Headphone Unit</p>
                </div>
                <div className="flex text-xs font-medium items-center gap-4">
                  <span className="text-primary w-[15px]">2x</span>
                  <p className="text-black/80">Replacement Cups</p>
                </div>
                <div className="flex text-xs font-medium items-center gap-4">
                  <span className="text-primary w-[15px]">1x</span>
                  <p className="text-black/80">User Manual</p>
                </div>
                <div className="flex text-xs font-medium items-center gap-4">
                  <span className="text-primary w-[15px]">1x</span>
                  <p className="text-black/80">3.55mm 5m Audio Cable</p>
                </div>
                <div className="flex text-xs font-medium items-center gap-4">
                  <span className="text-primary w-[15px]">1x</span>
                  <p className="text-black/80">Travel Bag</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-8 min-h-[40vh] pb-20">
            <p className="text-start text-xl font-bold capitalize">
              You may also like
            </p>
            <div className="relative w-full ">
              {/* <div className=" flex items-start bg-red-600X overflow-hiddenx justify-center md:justify-between gap-8 lg:gap-2 flex-wrapx"> */}
              <div
                className={`${
                  atStart ? "opacity-0" : "opacity-100"
                } pointer-events-none absolute z-70 inset-y-0 left-0 w-16 h-[220]x bg-red-700x bg-gradient-to-r from-white to-transparent`}
              />
              <div
                className={`${
                  atEnd ? "opacity-0" : "opacity-100"
                } pointer-events-none absolute inset-y-0 z-70 right-0 w-36 bg-gradient-to-l from-white to-transparent`}
              />
              <div
                ref={scrollerRef}
                id="scroll-container"
                className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pr-8"
              >
                {randomProducts?.map((product) => {
                  return (
                    <div
                      key={product.id}
                      onClick={() => got(product.title)}
                      className="flex flex-col gap-2 items-center justify-between pb-2 w-[200px] bg-secondary/40 h-[250px] group flex-shrink-0 rounded-lg cursor-pointer"
                    >
                      <div className="bg-secondary size-[200px] overflow-hidden rounded-md rounded-b-xl grid place-items-center group-hover:shadow-xl shadow-primary transition-shadow duration-300">
                        <img src={product.img} alt="" className="w-[150px]" />
                      </div>
                      <p className="font-semibold w-[200px] wrap mt-1 text-center text-sm line-clamp-2 h-[42px] group-hover:text-primary cursor-pointer">
                        {product.name}
                      </p>
                      {/* <button
                        onClick={() => got(product.title)}
                        className="bg-primary w-max mt-2 hover:bg-white text-white border border-primary hover:text-primary text-xs px-6 py-3 font-semibold uppercase tracking-wide"
                      >
                        See Product
                      </button> */}
                    </div>
                  );
                })}
              </div>
              <button
                className={`${
                  atStart ? "opacity-0 pointer-events-none" : "opacity-100"
                } absolute left-2 top-1/2 -translate-y-1/2 z-100 cursor-pointer bg-secondary p-3 rounded-full shadow-xl`}
                onClick={() => scrollBy(-step)}
                disabled={atStart}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="size-6 cursor-pointer z-60 relative rotate-180 text-primary"
                >
                  <path
                    fillRule="evenodd"
                    d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 0 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <button
                className={`${
                  atEnd ? "opacity-0 pointer-events-none" : "opacity-100"
                } absolute right-2 top-1/2 -translate-y-1/2 z-100 cursor-pointer bg-secondary p-3 rounded-full shadow-xl`}
                onClick={() => scrollBy(step)}
                disabled={atEnd}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="size-6 cursor-pointer z-60 relative text-primary"
                >
                  <path
                    fillRule="evenodd"
                    d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 0 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductView;
